/**
 * What OKANE counts about how it is used (docs/analytics-plan.md): an event's
 * name, and at most one label, from `schema/usage.json` — the same file the
 * server refuses everything else by, so the two cannot drift apart. A label is
 * a pattern matched whole; `""` means the event carries none.
 */
import RULES from '../../schema/usage.json'

const patterns = new Map(
  Object.entries(RULES)
    .filter(([name]) => !name.startsWith('//'))
    .map(([name, label]) => [name, label === '' ? null : new RegExp(`^(?:${label})$`)]),
)

/** Every name that may be counted. */
export const USAGE_EVENTS = [...patterns.keys()]

/** Whether this name may be counted with this label ('' for none). */
export function allowed(name, label = '') {
  if (!patterns.has(name)) return false
  const pattern = patterns.get(name)
  return pattern ? typeof label === 'string' && pattern.test(label) : label === ''
}

/** Every day from `from` to `to`, both included (`YYYY-MM-DD`). */
function daysBetween(from, to) {
  const days = []
  for (let d = new Date(`${from}T00:00:00Z`); d.toISOString().slice(0, 10) <= to; d.setUTCDate(d.getUTCDate() + 1)) {
    days.push(d.toISOString().slice(0, 10))
  }
  return days
}

/**
 * The server's report (`GET /api/usage`: `{ counts: [{ day, name, label, n }],
 * visitors: [{ day, n }] }`) shaped for the admin's view: every day of the
 * range — a quiet one as 0 — with its visitors and opens, and each event's
 * total with its labels, most first.
 */
export function summarise(report, from, to) {
  const days = daysBetween(from, to)
  const at = new Map(days.map((d, i) => [d, i]))
  const visitors = days.map(() => 0)
  const opens = days.map(() => 0)
  for (const v of report?.visitors ?? []) if (at.has(v.day)) visitors[at.get(v.day)] += v.n
  const events = new Map()
  for (const c of report?.counts ?? []) {
    if (!at.has(c.day)) continue
    if (c.name === 'open') opens[at.get(c.day)] += c.n
    const e = events.get(c.name) ?? { name: c.name, total: 0, labels: new Map() }
    e.total += c.n
    if (c.label) e.labels.set(c.label, (e.labels.get(c.label) ?? 0) + c.n)
    events.set(c.name, e)
  }
  const sum = (list) => list.reduce((a, b) => a + b, 0)
  return {
    days,
    visitors,
    opens,
    totals: { visitors: sum(visitors), opens: sum(opens), busiest: Math.max(0, ...visitors) },
    events: [...events.values()]
      .map((e) => ({ name: e.name, total: e.total, labels: [...e.labels].map(([label, n]) => ({ label, n })).sort((a, b) => b.n - a.n || a.label.localeCompare(b.label)) }))
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name)),
  }
}
